import { useCallback } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useUser } from '@/services/UserService'
import { useSafeTrack, useSafeAnalytics } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

const subscribeToEmailGroupMutation = gql`
  mutation SubscribeToEmailGroup($input: SubscribeToEmailGroupInput!) {
    subscribeToEmailGroup(input: $input) {
      success
    }
  }
`

export function useCaptureOrderEmail() {
  const analytics = useSafeAnalytics()
  const { query } = useSafeRouter()

  return useCallback(
    async function captureEmailAndSubscribe({
      subscriptionGroupId,
      email,
      position,
      projectSlug,
      ...customTrackPayload
    }: {
      subscriptionGroupId: string
      email: string
      position: string
      projectSlug: string
    } & Record<string, unknown>) {
      if (!email) {
        logger().warn('Email Capture: email is required', { projectSlug, position })
      }

      try {
        const user = analytics?.user?.()
        const userOrderEmail = user?.traits?.orderEmail
        if (email !== userOrderEmail) {
          await analytics.identify(user?.userId || null, {
            orderEmail: email,
          })
        }
      } catch (e) {
        logger().error('Email Capture: An error occurred while attempeting to identify a user for analytics', e)
      }

      await analytics.track('Email Captured', {
        slug: query.slug,
        position,
        subscriptionGroupId,
        email,
        projectSlug,
        ...customTrackPayload,
      })
    },
    [query.slug, analytics],
  )
}

export function useCaptureEmailAndSubscribe(disableSubscribe?: boolean) {
  const { userId } = useUser()
  const track = useSafeTrack()
  const { query } = useSafeRouter()
  const [subscribeToEmailGroup] = useMutation(subscribeToEmailGroupMutation, {
    errorPolicy: 'all',
  })

  return useCallback(
    async function captureEmailAndSubscribe({
      subscriptionGroupId,
      email,
      position,
      projectSlug,
      ...customTrackPayload
    }: {
      subscriptionGroupId?: string
      email: string
      position: string
      projectSlug: string
    } & Record<string, unknown>) {
      if (!email) {
        logger().warn('Email Capture: subscriptionGroupId is required', { projectSlug, position })
      }
      if (!subscriptionGroupId && !disableSubscribe) {
        logger().warn('Email Capture: subscriptionGroupId is required', { projectSlug, position })
      }

      track('Email Captured', {
        slug: query.slug,
        position,
        subscriptionGroupId,
        email,
        projectSlug,
        ...customTrackPayload,
      })

      return !disableSubscribe
        ? subscribeToEmailGroup({
            variables: {
              input: {
                userId: userId ?? null,
                email,
                subscriptionGroupId,
                projectSlug,
              },
            },
          })
        : Promise.resolve()
    },
    [track, query.slug, subscribeToEmailGroup, userId, disableSubscribe],
  )
}
